import { FileArchive, Share2 } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

import SheetShell from "../../components/SheetShell";
import { useConfirm } from "../confirm";
import { useMediaTasks } from "../mediaTasks";
import { scrollToTop } from "../scroll";
import { useToast } from "../toast";
import { shareBlockReasonOf, totalBytesOf, type ExportItem } from "./archive";
import {
  canShareFiles,
  downloadOriginal,
  exportAsZip,
  fetchAsFiles,
  saveBlob,
  shareFiles,
} from "./export";
import { formatBytes } from "./format";
import { withScreenAwake } from "./upload";

type Download = (loadItems: () => Promise<ExportItem[]>) => Promise<boolean>;

const MediaDownloadContext = createContext<Download | null>(null);

/** ZIP のファイル名。落とした日で区別できれば足りる */
function zipNameOf(): string {
  return `saranosuke-${new Date().toISOString().slice(0, 10)}.zip`;
}

/**
 * 共有シートが使えるか。端末とブラウザで決まるので一度だけ調べる。
 * モジュールの読み込み時ではなく初回参照時に調べる(テスト環境で navigator が
 * 無いことがある)。
 */
let shareable: boolean | null = null;
function shareAvailable(): boolean {
  shareable ??= canShareFiles();
  return shareable;
}

/**
 * 保存の進み具合。
 *
 * - `choose`: ZIP か共有シートかを選んでもらっている
 * - `preparing`: 共有シートに渡す実体を取っている
 * - `ready`: 取り終えて、あとは共有シートを開くだけ
 *
 * 共有シートは**タップと地続きでしか開けない**ため、取得と保存を分けてある。
 * 取得を待ってから開こうとすると弾かれる(→ export.ts の shareFiles)。
 */
interface Pending {
  items: ExportItem[];
  phase: "choose" | "preparing" | "ready";
  done: number;
  files: File[];
}

/**
 * 写真・動画を端末に保存する共通の流れ(保存のしかたを選ぶ → 取得 → 保存)。
 *
 * 全件(アルバムのメニュー)と選んだぶん(選択モード)で同じ手順を踏むため、
 * 画面ではなくここに置く。何を落とすのかを集める部分だけ呼ぶ側から渡す。
 *
 * 戻り値は「保存まで進んだか」。呼ぶ側が選択モードを抜けるかどうかの判断に使う
 * (取りやめたときに選択が消えると、選び直しになってしまう)。
 */
export function useMediaDownload(): Download {
  const download = useContext(MediaDownloadContext);
  if (download === null) throw new Error("MediaDownloadProvider の外では使えません");
  return download;
}

export function MediaDownloadProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { setExporting } = useMediaTasks();
  const [pending, setPending] = useState<Pending | null>(null);
  const decide = useRef<((saved: boolean) => void) | null>(null);
  // 準備の途中で閉じられたら、残りを取りにいかない
  const aborter = useRef<AbortController | null>(null);

  /** ZIP(1件なら原本のまま)。時間がかかるのでシートは閉じ、進み具合はヘッダーに出す */
  const runZip = useCallback(
    async (items: ExportItem[]): Promise<boolean> => {
      setExporting({ done: 0, total: items.length });
      // 進み具合は画面の上に出る。下を見ていると何も起きていないように見える
      scrollToTop();
      try {
        // 画面が消えると iOS は処理を止める。アップロードと同じ扱いにする
        await withScreenAwake(async () => {
          const only = items.length === 1 ? items[0] : undefined;
          if (only !== undefined) {
            await downloadOriginal(only);
            setExporting({ done: 1, total: 1 });
            return;
          }
          const blob = await exportAsZip(items, (done) => {
            setExporting({ done, total: items.length });
          });
          // 完了の合図はブラウザのダウンロード表示に任せる(トーストは重ねない)
          saveBlob(blob, zipNameOf());
        });
        return true;
      } catch (e) {
        console.error("ダウンロードに失敗", e);
        toast("ダウンロードに失敗しました。", "error");
        return false;
      } finally {
        setExporting(null);
      }
    },
    [setExporting, toast],
  );

  const download = useCallback<Download>(
    async (loadItems) => {
      let items: ExportItem[];
      try {
        items = await loadItems();
      } catch (e) {
        // 詳細はユーザーに出さない。原因はコンソールで追う
        console.error("ダウンロードの準備に失敗", e);
        toast("ダウンロードに失敗しました。", "error");
        return false;
      }
      if (items.length === 0) {
        toast("ダウンロード対象がありません。", "warn");
        return false;
      }

      // 共有シートを持たない端末(PC のブラウザ)は道が ZIP しかない。
      // 選択肢が1つだけのシートを出すより、今まで通り確認を取るほうが早い
      if (!shareAvailable()) {
        const ok = await confirm({
          // 動画が混ざると数GBになる。落とし始める前に大きさを見せる
          message: `${String(items.length)}件(約${formatBytes(totalBytesOf(items))})をダウンロードします。`,
          confirmLabel: "ダウンロード",
        });
        if (!ok) return false;
        return await runZip(items);
      }

      return new Promise<boolean>((resolve) => {
        // 前の問いが残っていたら、答えないまま捨てられるので取りやめ扱いにする
        decide.current?.(false);
        decide.current = resolve;
        setPending({ items, phase: "choose", done: 0, files: [] });
      });
    },
    [toast, confirm, runZip],
  );

  function finish(saved: boolean) {
    decide.current?.(saved);
    decide.current = null;
    aborter.current?.abort();
    aborter.current = null;
    setPending(null);
    setExporting(null);
  }

  /** 共有シートに渡す実体を取る。シートは開いたままにして、終わったら保存ボタンを出す */
  async function prepareShare(items: ExportItem[]) {
    const controller = new AbortController();
    aborter.current = controller;
    setPending({ items, phase: "preparing", done: 0, files: [] });
    // 進み具合はシートの中に出すが、離脱の確認はこちらが持っている(→ mediaTasks)
    setExporting({ done: 0, total: items.length });
    try {
      const files = await withScreenAwake(() =>
        fetchAsFiles(
          items,
          (done) => {
            setPending((current) => (current === null ? current : { ...current, done }));
            setExporting({ done, total: items.length });
          },
          controller.signal,
        ),
      );
      setExporting(null);
      setPending({ items, phase: "ready", done: items.length, files });
    } catch (e) {
      // 閉じたことによる中断は失敗ではない。後始末は finish が済ませている
      if (controller.signal.aborted) return;
      console.error("ダウンロードの準備に失敗", e);
      toast("ダウンロードに失敗しました。", "error");
      finish(false);
    }
  }

  /**
   * 共有シートを開く。**await を挟まずに呼ぶ**(タップから離れると開けない)。
   * 取りやめられたときはシートを開いたままにして、押し直せるようにする。
   */
  function openShareSheet(files: File[]) {
    shareFiles(files).then(
      (saved) => {
        if (saved) finish(true);
      },
      (e: unknown) => {
        console.error("共有シートを開けなかった", e);
        toast("保存に失敗しました。", "error");
        finish(false);
      },
    );
  }

  return (
    <MediaDownloadContext.Provider value={download}>
      {children}
      {pending !== null && (
        <SaveSheet
          pending={pending}
          onZip={() => {
            const { items } = pending;
            setPending(null);
            void runZip(items).then(finish);
          }}
          onShare={() => {
            void prepareShare(pending.items);
          }}
          onSave={() => {
            openShareSheet(pending.files);
          }}
          onClose={() => {
            finish(false);
          }}
        />
      )}
    </MediaDownloadContext.Provider>
  );
}

function SaveSheet({
  pending,
  onZip,
  onShare,
  onSave,
  onClose,
}: {
  pending: Pending;
  onZip: () => void;
  onShare: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const { items, phase, done, files } = pending;
  const single = items.length === 1;
  const blocked = shareBlockReasonOf(items);

  const rowClass =
    "flex w-full items-start gap-3 border-b border-[var(--color-line)] px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent";

  return (
    <SheetShell title="保存のしかたを選ぶ" onClose={onClose}>
      <p className="px-4 pt-4 text-sm text-[var(--color-sub)]">
        {String(items.length)}件(約{formatBytes(totalBytesOf(items))})を保存します。
      </p>

      {phase === "choose" && (
        <div className="mt-4 border-t border-[var(--color-line)]">
          {/* スマホでは共有シートが唯一「写真アプリに入る」道なので上に置く */}
          {shareAvailable() && (
            <button
              type="button"
              onClick={onShare}
              disabled={blocked !== null}
              className={rowClass}
            >
              <Share2 className="mt-0.5 size-4 shrink-0 text-[var(--color-sub)]" aria-hidden />
              <span>
                <span className="block text-sm">写真アプリなどへ保存</span>
                <span className="block text-xs text-[var(--color-sub)]">
                  {blocked ?? "共有シートが開きます。ZIPを展開せずに済みます。"}
                </span>
              </span>
            </button>
          )}
          <button type="button" onClick={onZip} className={rowClass}>
            <FileArchive className="mt-0.5 size-4 shrink-0 text-[var(--color-sub)]" aria-hidden />
            <span>
              <span className="block text-sm">
                {single ? "原本のまま保存" : "ZIPでまとめて保存"}
              </span>
              <span className="block text-xs text-[var(--color-sub)]">
                {single
                  ? "ファイルとして1件そのまま落とします。"
                  : "1つのファイルに束ねます。件数が多いときはこちら。"}
              </span>
            </span>
          </button>
        </div>
      )}

      {phase === "preparing" && (
        <p className="px-4 py-6 text-sm text-[var(--color-ink)]">
          読み込み中... {String(done)}/{String(items.length)}件
          <span className="mt-1 block text-xs text-[var(--color-sub)]">
            取り終えると保存できます。この画面のままお待ちください。
          </span>
        </p>
      )}

      {phase === "ready" && (
        <div className="px-4 py-4">
          <button
            type="button"
            onClick={onSave}
            className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-3 text-sm text-white transition hover:opacity-85"
          >
            {String(files.length)}件を保存
          </button>
          <p className="mt-2 text-xs text-[var(--color-sub)]">
            共有シートの「画像を保存」で写真アプリに入ります。
          </p>
        </div>
      )}
    </SheetShell>
  );
}
